import { Link } from 'react-router-dom';
import twintLogo from '@/assets/logo-twint.webp';
import applePayIcon from '@/assets/icons/apple-pay.webp';
import googlePayIcon from '@/assets/icons/google-pay.webp';
import { FileText, Shield, BookOpen, PenLine, Star, Sparkles, MessageCircle, Activity } from 'lucide-react';
import SocialIcon from '@/components/SocialIcon';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslations } from '@/hooks/useTranslations';
import { useStorefront, isLoyaltyDisabled } from '@/hooks/useStorefront';

const services = [
  { name: 'Instagram', slug: 'instagram' },
  { name: 'Tiktok', slug: 'tiktok' },
  { name: 'YouTube', slug: 'youtube' },
  { name: 'Twitter (X)', slug: 'twitter' },
  { name: 'Spotify', slug: 'spotify' },
  { name: 'Telegram', slug: 'telegram' },
  { name: 'Facebook', slug: 'facebook' },
  { name: 'LinkedIn', slug: 'linkedin' },
  { name: 'Twitch', slug: 'twitch' },
  { name: 'Threads', slug: 'threads' },
  { name: 'Soundcloud', slug: 'soundcloud' },
  { name: 'Reddit', slug: 'reddit' },
];

const socialLinks = [
  { name: 'Instagram', url: 'https://www.instagram.com/bigfollow.ch/', icon: (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
    </svg>
  )},
  { name: 'Twitter', url: 'https://x.com/Bigfollow_ch', icon: (
    <svg viewBox="0 0 24 24" className="h-6 w-6">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor" />
    </svg>
  )},
  { name: 'Facebook', url: 'https://www.facebook.com/Bigfollow.ch/', icon: (
    <svg viewBox="0 0 24 24" className="h-6 w-6">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="currentColor" />
    </svg>
  )},
  { name: 'LinkedIn', url: 'https://www.linkedin.com/company/bigfollow/', icon: (
    <svg viewBox="0 0 24 24" className="h-6 w-6">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor" />
    </svg>
  )},
];

const countryLinks = [
  { name: 'Bigfollow.it', flagCode: 'it', url: 'https://bigfollow.it' },
  { name: 'Bigfollow.at', flagCode: 'at', url: 'https://bigfollow.at' },
  { name: 'Bigfollow.se', flagCode: 'se', url: 'https://bigfollow.se' },
  { name: 'Bigfollow.al', flagCode: 'al', url: 'https://bigfollow.al' },
];

// Payment icons for dark footer background
const paymentIcons = [
  {
    name: 'TWINT',
    element: <img src={twintLogo} alt="TWINT" width="64" height="32" className="h-8 w-auto object-contain" />,
  },
  {
    name: 'Visa',
    element: (
      <svg viewBox="0 0 24 24" className="h-8 w-auto" fill="white">
        <path d="M9.112 8.262L5.97 15.758H3.92L2.374 9.775c-.094-.368-.175-.503-.461-.658C1.447 8.864.677 8.627 0 8.479l.046-.217h3.3a.904.904 0 01.894.764l.817 4.338 2.018-5.102zm8.033 5.049c.008-1.979-2.736-2.088-2.717-2.972.006-.269.262-.555.822-.628a3.66 3.66 0 011.913.336l.34-1.59a5.207 5.207 0 00-1.814-.333c-1.917 0-3.266 1.02-3.278 2.479-.012 1.079.963 1.68 1.698 2.04.756.367 1.01.603 1.006.931-.005.504-.602.725-1.16.734-.975.015-1.54-.263-1.992-.473l-.351 1.642c.453.208 1.289.39 2.156.398 2.037 0 3.37-1.006 3.377-2.564m5.061 2.447H24l-1.565-7.496h-1.656a.883.883 0 00-.826.55l-2.909 6.946h2.036l.405-1.12h2.488zm-2.163-2.656l1.02-2.815.588 2.815zm-8.16-4.84l-1.603 7.496H8.34l1.605-7.496z"/>
      </svg>
    ),
  },
  {
    name: 'Mastercard',
    element: (
      <svg viewBox="0 0 24 24" className="h-8 w-auto">
        <circle cx="9" cy="12" r="7" fill="#EB001B" />
        <circle cx="15" cy="12" r="7" fill="#F79E1B" />
        <path d="M12 6.68a6.97 6.97 0 012.61 5.32 6.97 6.97 0 01-2.61 5.32A6.97 6.97 0 019.39 12 6.97 6.97 0 0112 6.68z" fill="#FF5F00" />
      </svg>
    ),
  },
  {
    name: 'Apple Pay',
    element: <img src={applePayIcon} alt="Apple Pay" width="56" height="28" className="h-7 w-auto object-contain" />,
  },
  {
    name: 'Google Pay',
    element: <img src={googlePayIcon} alt="Google Pay" width="56" height="28" className="h-7 w-auto object-contain" />,
  },
  {
    name: 'Klarna',
    element: (
      <svg viewBox="0 0 24 24" className="h-8 w-auto">
        <path fill="#FFB3C7" d="M4.592 2v20H0V2h4.592zm11.46 0c0 4.194-1.583 8.105-4.415 11.068l-.278.283L17.702 22h-5.668l-6.893-9.4 1.779-1.332c2.858-2.14 4.535-5.378 4.637-8.924L11.562 2h4.49zM21.5 17a2.5 2.5 0 110 5 2.5 2.5 0 010-5z"/>
      </svg>
    ),
  },
];

export default function Footer() {
  const { localePath } = useLanguage();
  const { t } = useTranslations();
  const { storefront } = useStorefront();
  const isAT = storefront === 'at';
  const isIT = storefront === 'it';
  const isAL = storefront === 'al';
  const paymentIconsForSf = (isAT || isIT || isAL)
    ? paymentIcons.filter((p) => p.name !== 'TWINT')
    : paymentIcons;
  // AT/IT/AL run without TWINT — the copy must name the methods that are
  // actually offered there. Sourced from ui_translations, not inline locales.
  const paymentDescription = isAT
    ? 'Beliebte Zahlungsarten wie Visa, Mastercard, PayPal, Apple Pay oder Klarna'
    : isIT
      ? 'Metodi di pagamento popolari come Visa, Mastercard, PayPal, Apple Pay o Klarna'
      : t('footer.payment_desc');

  const footerLinks = [
    { name: t('footer.big_points'), to: localePath('/big-punkte'), icon: Sparkles, loyalty: true },
    { name: t('footer.contact'), to: localePath('/kontakt'), icon: MessageCircle },
    { name: t('footer.terms'), to: localePath('/agb'), icon: FileText },
    { name: t('footer.privacy'), to: localePath('/datenschutz'), icon: Shield },
    { name: t('footer.imprint'), to: localePath('/impressum'), icon: BookOpen },
    { name: t('nav.blog'), to: localePath('/blog'), icon: PenLine },
    { name: t('footer.sitemap'), to: localePath('/sitemap'), icon: FileText },
    { name: t('footer.status'), to: localePath('/status'), icon: Activity },
  ]
    // Loyalty link follows the shared gate list (IT, AL, …) — no per-case check.
    .filter((link) => !(link.loyalty && isLoyaltyDisabled(storefront)));

  return (
    <footer className="bg-[hsl(242,45%,25%)] text-white">
      {/* Main footer */}
      <div className="container py-12">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Column 1: Logo & description */}
          <div>
            <Link to={localePath('/')} className="mb-4 inline-block">
              <img
                src="/logo-white.png"
                alt="BigFollow"
                width="163"
                height="40"
                decoding="async"
                loading="lazy"
                className="h-8 w-auto"
                style={{ aspectRatio: '1157 / 284' }}
              />
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-white/70">
              {t('footer.description')}
            </p>
            {/* Social icons – all nofollow */}
            <div className="mt-5 flex items-center gap-3">
              {socialLinks.map(social => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-white/70 transition-colors hover:text-white"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Links */}
          <div>
            <p className="mb-4 text-lg font-bold">{t('footer.links')}</p>
            <ul className="space-y-3">
              {footerLinks.map(link => (
                <li key={link.to + link.name}>
                  <Link to={link.to} className="inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-white">
                    <link.icon className="h-4 w-4" /> {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <p className="mb-4 text-lg font-bold">{t('footer.services')}</p>
            <ul className="space-y-2.5">
              {services.map(service => (
                <li key={service.slug}>
                  <Link to={`/${service.slug}`} className="inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-white">
                    <SocialIcon slug={service.slug} className="h-4 w-4 shrink-0" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Payment methods */}
          <div>
            <p className="mb-4 text-lg font-bold">{t('footer.payment_methods')}</p>
            <div className="flex flex-wrap items-center gap-4">
              {paymentIconsForSf.map(icon => (
                <div key={icon.name} title={icon.name} className="flex items-center">
                  {icon.element}
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-white/70">
              {paymentDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Country links bar */}
      <div className="border-t border-white/10">
        <div className="container flex flex-wrap items-center gap-6 py-4">
          {countryLinks.map(country => (
            <a
              key={country.url}
              href={country.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-white/70 transition-colors hover:text-white"
            >
              <img src={`/flags/${country.flagCode.toUpperCase()}.svg`} width="20" height="15" alt={country.name} className="inline-block rounded-[2px]" /> {country.name}
            </a>
          ))}
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-white/10">
        <div className="container py-4 text-center text-sm text-white/70">
          © {new Date().getFullYear()}{' '}
          <a href="https://portalweb.ch" target="_blank" rel="noopener noreferrer" className="underline text-white/90 hover:text-white">
            PortalWeb GmbH
          </a>
          {' '}– {t('footer.copyright')}
        </div>
      </div>
    </footer>
  );
}
